import { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation, Outlet } from 'react-router-dom';
import Cookies from 'js-cookie';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import {
  IconHome2,
  IconPackage,
  IconTruckDelivery,
  IconCalendarStats,
  IconLogout,
  IconUser,
  IconSettings,
  IconHelp,
  IconChevronDown,
  IconGavel,
  IconMenu2,
} from '@tabler/icons-react';
import { useProfile } from '../contexts/ProfileContext';
import { useNavbarCounts } from '../hooks/useNavbarCounts';
import { useComputedColorScheme, useMantineTheme, Paper, rem, Group, UnstyledButton, Center, Stack, Box, Badge, Menu, AppShell, Burger, ActionIcon, Avatar, Divider, Flex, Button, Text, NavLink } from '@mantine/core';

const DashboardLayout: React.FC<{ children?: React.ReactNode }> = () => {
  const { isProfileComplete } = useProfile();
  const [opened, { toggle }] = useDisclosure();
  // const { setColorScheme } = useMantineColorScheme();
  const computedColorScheme = useComputedColorScheme('light');
  const [userMenuOpened, setUserMenuOpened] = useState(false);
  const theme = useMantineTheme();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const isTablet = useMediaQuery('(max-width: 1024px)');

  // Get navbar counts
  const { receivedPackages, pendingShipments } = useNavbarCounts();

  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState({
    name: 'Guest User',
    email: 'user@example.com',
    image: '',
  });

  // const toggleColorScheme = () => {
  //   setColorScheme(computedColorScheme === 'light' ? 'dark' : 'light');
  // };

  // Load user information from session storage
  useEffect(() => {
    try {
      const storedUserInfo = sessionStorage.getItem("userInfo");

      if (storedUserInfo) {
        let userInfoData;
        try {
          userInfoData = JSON.parse(atob(storedUserInfo));
        } catch (base64Error) {
          userInfoData = JSON.parse(storedUserInfo);
        }

        if (userInfoData) {
          setUser({
            name: `${userInfoData.given_name || ''} ${userInfoData.family_name || ''}`.trim() || 'User',
            email: userInfoData.email || 'user@example.com',
            image: userInfoData.picture || '',
          });
        }
      }
    } catch (error) {
    }
  }, []);

  // Modify menu items to be disabled when profile is not complete
  const menuItems = [
    {
      label: 'Dashboard',
      icon: <IconHome2 size={isMobile ? "1.4rem" : "1.2rem"} stroke={1.5} />,
      to: '/dashboard',
      description: 'Overview & stats',
      disabled: !isProfileComplete
    },
    {
      label: 'Packages',
      icon: <IconPackage size={isMobile ? "1.4rem" : "1.2rem"} stroke={1.5} />,
      to: '/packages',
      description: 'Manage packages',
      count: receivedPackages,
      showCount: receivedPackages > 0,
      disabled: !isProfileComplete
    },
    {
      label: 'Shipments',
      icon: <IconTruckDelivery size={isMobile ? "1.4rem" : "1.2rem"} stroke={1.5} />,
      to: '/shipments',
      description: 'Track shipments',
      count: pendingShipments,
      showCount: pendingShipments > 0,
      disabled: !isProfileComplete
    }
  ];

  const allMenuItems = [
    ...menuItems,
    {
      label: 'Expected',
      icon: <IconCalendarStats size={isMobile ? "1.4rem" : "1.2rem"} stroke={1.5} />,
      to: '/expected',
      description: 'Expected packages',
      disabled: !isProfileComplete
    },
    {
      label: 'Rules',
      icon: <IconGavel size={isMobile ? "1.4rem" : "1.2rem"} stroke={1.5} />,
      to: '/rules-and-regulations',
      description: 'Regulations',
      disabled: !isProfileComplete
    }
  ];

  const secondaryMenuItems = [
    { label: 'Profile', icon: <IconUser size="1.2rem" stroke={1.5} />, to: '/profile' },
    { label: 'Help & Support', icon: <IconHelp size="1.2rem" stroke={1.5} />, to: '/help' },
  ];

  // Mobile Bottom Navigation Component
  const MobileBottomNav = () => {
    return (
      <Paper
        shadow="lg"
        radius={0}
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          borderTop: `1px solid ${computedColorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[2]}`,
          backgroundColor: '#e0edffff',
          height: rem(70),
        }}
        p="xs"
      >
        <Group justify="space-around" gap={0} h="100%">
          {/* Main Navigation Items */}
          {menuItems.map((item) => {
            const isActive = location.pathname === item.to || location.pathname.startsWith(item.to + '/');
            return (
              <UnstyledButton
                key={item.to}
                component={Link}
                to={item.disabled ? '#' : item.to}
                onClick={(e) => {
                  if (item.disabled) {
                    e.preventDefault();
                    navigate('/profile');
                  }
                }}
                style={{
                  padding: rem(8),
                  borderRadius: theme.radius.md,
                  minWidth: rem(70),
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 150ms ease',
                  opacity: item.disabled ? 0.5 : 1,
                  cursor: item.disabled ? 'not-allowed' : 'pointer',
                  backgroundColor: isActive ? '#f8f8f8ff' : 'transparent',
                }}
                title={item.disabled ? "Complete your profile to access this feature" : ""}
              >
                <Center>
                  <Stack gap={4} align="center">
                    <Box
                      style={{
                        color: item.disabled ? (computedColorScheme === 'dark' ? theme.colors.gray[7] : theme.colors.gray[4]) :
                          isActive
                            ? '#3584e4'
                            : computedColorScheme === 'dark' ? theme.colors.gray[5] : theme.colors.gray[6],
                        transition: 'color 150ms ease',
                        position: 'relative',
                      }}
                    >
                      {item.icon}
                      {item.showCount && (
                        <Badge
                          size="xs"
                          variant="filled"
                          color={item.disabled ? "gray" : "red"}
                          style={{
                            position: 'absolute',
                            top: -8,
                            right: -12,
                            minWidth: rem(16),
                            height: rem(16),
                            fontSize: rem(10),
                            padding: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: item.disabled ? 0.5 : 1,
                          }}
                        >
                          {item.count}
                        </Badge>
                      )}
                    </Box>
                    <Text
                      size="xs"
                      fw={isActive ? 600 : 400}
                      c={item.disabled ? (computedColorScheme === 'dark' ? 'gray.7' : 'gray.4') :
                        isActive ? '#3584e4' : 'dimmed'}
                      ta="center"
                      style={{ lineHeight: 1 }}
                    >
                      {item.label}
                      {item.disabled && (
                        <Text size="9px" c="dimmed" style={{ opacity: 0.7 }}>
                          Complete profile first
                        </Text>
                      )}
                    </Text>
                  </Stack>
                </Center>
              </UnstyledButton>
            );
          })}

          {/* More Menu Button */}
          <Menu shadow="md" width={240} position="top-end">
            <Menu.Target>
              <UnstyledButton
                style={{
                  padding: rem(8),
                  borderRadius: theme.radius.md,
                  minWidth: rem(70),
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Center>
                  <Stack gap={4} align="center">
                    <IconMenu2
                      size="1.4rem"
                      stroke={1.5}
                      color={computedColorScheme === 'dark' ? theme.colors.gray[5] : theme.colors.gray[6]}
                    />
                    <Text size="xs" c="dimmed" ta="center" style={{ lineHeight: 1 }}>
                      More
                    </Text>
                  </Stack>
                </Center>
              </UnstyledButton>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Label>Additional Pages</Menu.Label>
              <Menu.Item
                leftSection={<IconCalendarStats size={16} />}
                component={Link}
                to={!isProfileComplete ? '#' : "/expected"}
                onClick={(e) => {
                  if (!isProfileComplete) {
                    e.preventDefault();
                    navigate('/profile');
                  }
                }}
                disabled={!isProfileComplete}
                style={{
                  opacity: !isProfileComplete ? 0.5 : 1,
                  cursor: !isProfileComplete ? 'not-allowed' : 'pointer',
                }}
              >
                Expected Packages
              </Menu.Item>
              <Menu.Item
                leftSection={<IconGavel size={16} />}
                component={Link}
                to={!isProfileComplete ? '#' : "/rules-and-regulations"}
                onClick={(e) => {
                  if (!isProfileComplete) {
                    e.preventDefault();
                    navigate('/profile');
                  }
                }}
                disabled={!isProfileComplete}
                style={{
                  opacity: !isProfileComplete ? 0.5 : 1,
                  cursor: !isProfileComplete ? 'not-allowed' : 'pointer',
                }}
              >
                Rules & Regulations
              </Menu.Item>
              <Menu.Divider />
              <Menu.Label>Account</Menu.Label>
              {secondaryMenuItems.map((item) => (
                <Menu.Item
                  key={item.to}
                  leftSection={item.icon}
                  component={Link}
                  to={item.to}
                >
                  {item.label}
                </Menu.Item>
              ))}
              <Menu.Divider />
              <Menu.Item
                leftSection={<IconSettings size={16} />}
                onClick={() => navigate('/settings')}
              >
                Settings
              </Menu.Item>
              {/* <Menu.Item
                leftSection={computedColorScheme === 'light' ? <IconMoonStars size={16} /> : <IconSun size={16} />}
                onClick={toggleColorScheme}
              >
                {computedColorScheme === 'light' ? 'Dark Mode' : 'Light Mode'}
              </Menu.Item> */}
              <Menu.Divider />
              <Menu.Item
                leftSection={<IconLogout size={16} />}
                color="red"
                onClick={async () => {
                  const sessionHint = Cookies.get('session_hint');
                  window.location.href = `/auth/logout?session_hint=${sessionHint || ''}`;
                  Object.keys(Cookies.get()).forEach(cookieName => {
                    Cookies.remove(cookieName);
                  });
                  sessionStorage.clear();
                }}
              >
                Logout
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Paper>
    );
  }
  return (
    <AppShell
      header={{ height: isMobile ? 70 : 60 }}
      navbar={isMobile ? undefined : {
        width: isTablet ? 280 : 300,
        breakpoint: 'sm',
        collapsed: { mobile: !opened }
      }}
      padding={isMobile ? { top: 'md', bottom: 90, left: 'sm', right: 'sm' } : 'md'}
    >
      <AppShell.Header>
        <Group
          h="100%"
          px={isMobile ? "sm" : "md"}
          justify="space-between"
          style={{ borderBottom: `1px solid ${computedColorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[2]}` }}
        >
          <Group>
            {!isMobile && <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />}
            <UnstyledButton onClick={() => navigate('/home')}>
              <Group gap={6} align="center">
                <img
                  src="https://otl.nyc3.cdn.digitaloceanspaces.com/fe/Logo-old.jpeg"
                  alt="OTLExpress"
                  style={{
                    height: isMobile ? '32px' : '36px',
                  }}
                />
                <Text
                  size="sm"
                  c="#2F3192"
                  style={{
                    fontWeight: 700,
                    fontSize: isMobile ? '1.1rem' : '1.25rem',
                    letterSpacing: '0.5px',
                    marginLeft: '4px',
                  }}
                >
                  Express
                </Text>
              </Group>
            </UnstyledButton>
          </Group>

          <Group gap={isMobile ? "xs" : "sm"}>
            {/* Mobile-optimized header actions */}
            {isMobile ? (
              <>

                <Menu
                  shadow="md"
                  width={280}
                  position="bottom-end"
                  onOpen={() => setUserMenuOpened(true)}
                  onClose={() => setUserMenuOpened(false)}
                >
                  <Menu.Target>
                    <ActionIcon variant="subtle" radius="xl" size="lg">
                      <Avatar color="blue" radius="xl" size="md">
                        {user?.name?.charAt(0) || 'U'}
                      </Avatar>
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Label>
                      <Stack gap={4}>
                        <Text fw={500} size="sm">{user?.name || 'User'}</Text>
                        <Text c="dimmed" size="xs">{user?.email || 'user@example.com'}</Text>
                      </Stack>
                    </Menu.Label>
                    <Menu.Divider />
                    {/* <Menu.Item
                      leftSection={computedColorScheme === 'light' ? <IconMoonStars size={14} /> : <IconSun size={14} />}
                      onClick={toggleColorScheme}
                    >
                      {computedColorScheme === 'light' ? 'Dark Mode' : 'Light Mode'}
                    </Menu.Item> */}
                    <Menu.Item
                      leftSection={<IconUser size={14} />}
                      onClick={() => navigate('/profile')}
                    >
                      Profile
                    </Menu.Item>
                    <Menu.Item
                      leftSection={<IconSettings size={14} />}
                      onClick={() => navigate('/settings')}
                    >
                      Settings
                    </Menu.Item>
                    <Menu.Divider />
                    <Menu.Item
                      leftSection={<IconLogout size={14} />}
                      color="red"
                      onClick={async () => {
                        const sessionHint = Cookies.get('session_hint');
                        window.location.href = `/auth/logout?session_hint=${sessionHint || ''}`;
                        Object.keys(Cookies.get()).forEach(cookieName => {
                          Cookies.remove(cookieName);
                        });
                        sessionStorage.clear();
                      }}
                    >
                      Logout
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              </>
            ) : (
              // Desktop header actions
              <>
                {/* <Tooltip label={computedColorScheme === 'light' ? 'Dark mode' : 'Light mode'}>
                  <ActionIcon
                    variant="subtle"
                    radius="xl"
                    size="lg"
                    onClick={toggleColorScheme}
                    color={computedColorScheme === 'light' ? 'gray' : 'yellow'}
                  >
                    {computedColorScheme === 'light' ? (
                      <IconMoonStars size="1.2rem" stroke={1.5} />
                    ) : (
                      <IconSun size="1.2rem" stroke={1.5} />
                    )}
                  </ActionIcon>
                </Tooltip> */}


                <Menu
                  shadow="md"
                  width={200}
                  position="bottom-end"
                  onOpen={() => setUserMenuOpened(true)}
                  onClose={() => setUserMenuOpened(false)}
                >
                  <Menu.Target>
                    <UnstyledButton
                      px={8}
                      py={5}
                      style={{
                        borderRadius: '2rem',
                        transition: 'background-color 150ms ease',
                        backgroundColor: userMenuOpened ?
                          (computedColorScheme === 'dark' ? '#2C2E33' : '#E9ECEF') :
                          'transparent'
                      }}
                    >
                      <Group gap={7} justify="center">
                        <Avatar color="blue" radius="xl">
                          {user?.name?.charAt(0) || 'U'}
                        </Avatar>
                        <Box style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }} visibleFrom="sm">
                          <Text fw={500} size="sm" lh={1} mr={3}>
                            {user?.name || 'User'}
                          </Text>
                          <Text c="dimmed" size="xs">{user?.email || 'user@example.com'}</Text>
                        </Box>
                        <IconChevronDown size="1rem" stroke={1.5} />
                      </Group>
                    </UnstyledButton>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Label>Account</Menu.Label>
                    <Menu.Item
                      leftSection={<IconUser size={12} />}
                      onClick={() => navigate('/profile')}
                    >
                      Profile
                    </Menu.Item>
                    <Menu.Item
                      leftSection={<IconSettings size={12} />}
                      onClick={() => navigate('/settings')}
                    >
                      Settings
                    </Menu.Item>
                    <Menu.Divider />
                    <Menu.Item
                      leftSection={<IconLogout size={12} />}
                      color="red"
                      onClick={async () => {
                        const sessionHint = Cookies.get('session_hint');
                        window.location.href = `/auth/logout?session_hint=${sessionHint || ''}`;
                        Object.keys(Cookies.get()).forEach(cookieName => {
                          Cookies.remove(cookieName);
                        });
                        sessionStorage.clear();
                      }}
                    >
                      Logout
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              </>
            )}
          </Group>
        </Group>
      </AppShell.Header>

      {/* Desktop Navbar */}
      {!isMobile && (
        <AppShell.Navbar p="md">
          <AppShell.Section grow>
            <Stack gap="xs">
              {allMenuItems.map((item) => {
                const isActive = location.pathname === item.to || location.pathname.startsWith(item.to + '/');
                // const showCount = item.showCount && item.count > 0;

                return (
                  <NavLink
                    key={item.to}
                    label={
                      <Group justify="space-between" wrap="nowrap">
                        <Text>{item.label}</Text>
                        {item.count && (
                          <Badge
                            size="sm"
                            variant="filled"
                            color="red"
                            style={{
                              minWidth: rem(20),
                              height: rem(20),
                              fontSize: rem(11),
                            }}
                          >
                            {item.count}
                          </Badge>
                        )}
                      </Group>
                    }
                    description={item.description}
                    leftSection={item.icon}
                    active={isActive}
                    disabled={item.disabled}
                    color={isActive ? "blue" : "gray"}
                    component={Link}
                    style={{
                      textDecoration: 'none',
                      borderRadius: theme.radius.md,
                      opacity: item.disabled ? 0.5 : 1,
                      cursor: item.disabled ? 'not-allowed' : 'pointer',
                    }}
                    to={item.disabled ? '#' : item.to}
                    onClick={(e) => {
                      if (item.disabled) {
                        e.preventDefault();
                        return;
                      }
                      if (opened) {
                        toggle();
                      }
                    }}
                    fw={500}
                    p="md"
                  />
                );
              })}

              {secondaryMenuItems.map((item) => (
                <NavLink
                  key={item.to}
                  label={item.label}
                  leftSection={item.icon}
                  active={location.pathname === item.to || location.pathname.startsWith(item.to + '/')}
                  // variant={location.pathname === item.to ? "filled" : "light"}
                  component={Link}
                  style={{
                    textDecoration: 'none',
                    borderRadius: theme.radius.md,
                  }}
                  to={item.to}
                  onClick={() => {
                    if (opened) {
                      toggle();
                    }
                  }}
                  fw={500}
                  p="md"
                />
              ))}
            </Stack>
          </AppShell.Section>
          <AppShell.Section hiddenFrom='sm'>
            <Divider my="sm" />
            <Flex direction="column" gap="sm" px="sm" py="md">
              <Button
                variant="subtle"
                color="gray"
                leftSection={<IconLogout size="1.1rem" stroke={1.5} />}
                size="md"
                fullWidth
                onClick={() => {
                  const sessionHint = Cookies.get('session_hint');
                  if (sessionHint) {
                    window.location.href = `/auth/logout?session_hint=${sessionHint || ''}`;
                    Object.keys(Cookies.get()).forEach(cookieName => {
                      Cookies.remove(cookieName);
                    });
                    sessionStorage.clear();
                  }
                }}
                radius="md"
              >
                Log Out
              </Button>
              <Text size="xs" c="dimmed" ta="center" mt={5}>
                Signed in as {user?.name || 'User'}
              </Text>
            </Flex>
          </AppShell.Section>
        </AppShell.Navbar>
      )}

      <AppShell.Main
        style={{
          minHeight: isMobile ? 'calc(100vh - 140px)' : 'auto',
          paddingBottom: isMobile ? rem(30) : undefined,
          paddingTop: isMobile ? rem(30) : undefined,
          overflowY: isMobile ? 'auto' : undefined,
        }}
      >
        <Box style={{
          paddingBottom: isMobile ? rem(30) : undefined,
          paddingTop: isMobile ? rem(30) : undefined,
        }}>
          {!isProfileComplete && location.pathname !== '/profile' && (
            <Paper p="xs" mb="md" bg="yellow.0" withBorder style={{ borderColor: theme.colors.yellow[3] }}>
              <Group gap="sm" align="flex-start">
                <Box color="yellow.7" mt={4}>
                  <IconUser size={16} />
                </Box>
                <div>
                  <Text fw={500} size="sm" c="yellow.9">Complete your profile</Text>
                  <Text size="xs" c="yellow.8" mt={2}>
                    Please complete your profile information to access all features
                  </Text>
                  <Button
                    variant="light"
                    color="yellow"
                    size="xs"
                    mt="xs"
                    onClick={() => navigate('/profile')}
                  >
                    Update Profile
                  </Button>
                </div>
              </Group>
            </Paper>
          )}
          <Outlet />
        </Box>
      </AppShell.Main>

      {/* Mobile Bottom Navigation */}
      {isMobile && (
        <>
          {!isProfileComplete && location.pathname !== '/profile' && (
            <Paper
              shadow="md"
              radius={0}
              style={{
                position: 'fixed',
                bottom: rem(70),
                left: 0,
                right: 0,
                zIndex: 999,
                backgroundColor: theme.colors.yellow[0],
                borderTop: `1px solid ${theme.colors.yellow[3]}`,
              }}
              p="xs"
            >
              <Group gap="xs" >
                <Text size="xs" fw={500} c="yellow.9">Complete your profile to access all features</Text>
                <Button
                  variant="filled"
                  color="yellow"
                  size="xs"
                  onClick={() => navigate('/profile')}
                >
                  Go to Profile
                </Button>
              </Group>
            </Paper>
          )}
          <MobileBottomNav />
        </>
      )}
    </AppShell>
  );
};

export default DashboardLayout;
